"use client";

// Built on Base UI's Form — see full API at:
// https://base-ui.com/react/components/form

import React from "react";
import { Form as BaseForm } from "@base-ui/react/form";

import { useAutofocusFirstField } from "../use-autofocus-first-field";

export type RootProps = BaseForm.Props & {
  /**
   * Puts the cursor in the form's first usable field on mount, so the page is
   * ready to type into. Opt-in, because focusing a form that starts below the
   * fold scrolls the page out from under whatever the visitor was reading —
   * only turn it on where the form is the point of the page.
   *
   * Ignored on touch devices, where it would raise the keyboard uninvited. A
   * form inside a modal needs nothing here; the modal handles its own focus.
   */
  autoFocusFirstField?: boolean;
};

/**
 * Honeypot field name for spam detection.
 * Use this constant in backend validation to check if the field has a value.
 */
export const HONEYPOT_FIELD_NAME = "contact_preference_details";

/**
 * Hidden field for spam detection. Bots fill all visible fields; the server
 * rejects submissions where this field has a value.
 *
 * `Form.Root` already renders this automatically — use the standalone export
 * only for forms that don't use `Form.Root`.
 */
export function HoneypotField() {
  return (
    <div
      style={{
        position: "absolute",
        left: "-9999px",
        width: "1px",
        height: "1px",
        opacity: 0,
        pointerEvents: "none",
      }}
      aria-hidden="true"
    >
      <label htmlFor={HONEYPOT_FIELD_NAME}>Please leave this field blank</label>
      <input
        type="text"
        name={HONEYPOT_FIELD_NAME}
        id={HONEYPOT_FIELD_NAME}
        tabIndex={-1}
        autoComplete="off"
        defaultValue=""
      />
    </div>
  );
}

/**
 * A form that manages field validation and error state.
 *
 * Wraps Base UI's `Form`, which provides the validation lifecycle that
 * `Field.Root` (and the `Input` primitive) depends on. Without this wrapper,
 * `Field.Error` will never render because `field.validate()` is not called on submit.
 *
 * Defaults to `validationMode="onBlur"` so that fields are validated when
 * the user leaves them. This ensures async validation results (e.g. business
 * email checks) are resolved before the user reaches the submit button.
 * Base UI's Form reads validation state synchronously on submit, so
 * `onSubmit` mode can't block on async validators.
 *
 * @see https://base-ui.com/react/components/form
 */
export function Root({
  children,
  validationMode = "onBlur",
  autoFocusFirstField = false,
  ref,
  ...rest
}: RootProps) {
  const autofocus = useAutofocusFirstField({ active: autoFocusFirstField });
  const { contentRef } = autofocus;

  // Attaching the hook's ref sets state, so a fresh function each render would reattach forever.
  const setRef = React.useCallback(
    (node: HTMLFormElement | null) => {
      contentRef(node);

      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    },
    [contentRef, ref],
  );

  return (
    <BaseForm validationMode={validationMode} ref={setRef} {...rest}>
      {children}
      <HoneypotField />
    </BaseForm>
  );
}
